/**
 * A2A Agents Page (SWRM-134)
 * Manage Agent-to-Agent protocol published agents
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Plus,
  Globe,
  Copy,
  ExternalLink,
  Trash2,
  CheckCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { apiService } from '../services/api';
import { useNotificationStore } from '../store';
import { formatApiError } from '../utils/errorFormatter';
import { AddA2AAgentModal } from '../components/A2AAgents/AddA2AAgentModal';

interface A2AAgent {
  name: string;
  slug: string;
  description: string;
  version: string;
  agent_card_url: string;
  tasks_endpoint: string;
  pipeline_id?: number;
  capabilities?: string[];
  input_schema?: {
    type: string;
    properties: Record<string, any>;
    required: string[];
  };
}

// Helper function to generate curl command
const generateCurlCommand = (
  agent: A2AAgent,
  allInputs: Record<string, any>
): string => {
  // Build input object with example values
  const inputObj: Record<string, string> = {};
  Object.entries(allInputs).forEach(([key, schema]: [string, any]) => {
    if (schema.type === 'string') {
      inputObj[key] = `"Example ${key}"`;
    } else if (schema.type === 'list' || schema.type === 'file') {
      inputObj[key] = '[{"filename": "example.txt", "content": "..."}]';
    } else {
      inputObj[key] = `"value"`;
    }
  });

  const payload = {
    input: inputObj,
    webhook_url: 'https://your-system.com/webhooks/callback',
    correlation_id: 'your-tracking-id-123',
  };

  const jsonPayload = JSON.stringify(payload, null, 2);

  return `curl -X POST '${agent.tasks_endpoint}' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_API_KEY' \\
  -d '${jsonPayload}'`;
};

export const A2AAgents: React.FC = () => {
  const { addNotification } = useNotificationStore();

  // State
  const [agents, setAgents] = useState<A2AAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [removingAgent, setRemovingAgent] = useState<string | null>(null);
  const [expandedAgents, setExpandedAgents] = useState<Set<string>>(new Set());

  const loadAgents = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getA2AAgents();
      setAgents(data.agents || []);
    } catch (error: any) {
      console.error('Error loading A2A agents:', error);
      addNotification(
        `Failed to load A2A agents: ${formatApiError(error)}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadAgents();
  }, [loadAgents]);

  const handleCopyURL = (url: string) => {
    navigator.clipboard.writeText(url);
    addNotification('Agent card URL copied to clipboard', 'success');
  };

  const toggleExpanded = (slug: string) => {
    setExpandedAgents((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(slug)) {
        newSet.delete(slug);
      } else {
        newSet.add(slug);
      }
      return newSet;
    });
  };

  const handleRemoveAgent = async (slug: string) => {
    if (
      !window.confirm(
        'Are you sure you want to unpublish this agent? The pipeline will not be deleted, but it will no longer be accessible via A2A protocol.'
      )
    ) {
      return;
    }

    try {
      setRemovingAgent(slug);
      await apiService.unpublishA2AAgent(slug);
      addNotification('Agent unpublished successfully', 'success');
      loadAgents();
    } catch (error: any) {
      console.error('Error unpublishing agent:', error);
      addNotification(
        `Failed to unpublish agent: ${formatApiError(error)}`,
        'error'
      );
    } finally {
      setRemovingAgent(null);
    }
  };

  const handleAgentAdded = () => {
    setShowAddModal(false);
    loadAgents();
    addNotification('Agent published successfully', 'success');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                A2A Agents
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Publish pipelines as Agent-to-Agent protocol endpoints
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={loadAgents}
                disabled={isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                title="Refresh agents"
              >
                <RefreshCw
                  className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`}
                />
                Refresh
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Publish Agent
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-8">
        {/* Loading State */}
        {isLoading && agents.length === 0 && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
            <span className="ml-3 text-lg text-gray-600 dark:text-gray-400">
              Loading agents...
            </span>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && agents.length === 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Globe className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No published agents
            </h3>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Publish your first pipeline as an A2A agent to make it accessible
              to external systems.
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-5 h-5" />
              Publish Your First Agent
            </button>
          </div>
        )}

        {/* Agents Grid */}
        {agents.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {agents.map((agent) => {
              const isExpanded = expandedAgents.has(agent.slug);
              const requiredInputs = agent.input_schema?.required || [];
              const allInputs = agent.input_schema?.properties || {};

              return (
                <div
                  key={agent.slug}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="p-6">
                    {/* Agent Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                          <Globe className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900 dark:text-white">
                            {agent.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            v{agent.version}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-xs text-green-600 dark:text-green-400">
                          Active
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 line-clamp-2">
                      {agent.description}
                    </p>

                    {/* Slug */}
                    <div className="mb-4">
                      <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">
                        Slug
                      </p>
                      <code className="text-sm bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                        {agent.slug}
                      </code>
                    </div>

                    {/* Expand/Collapse Button */}
                    <button
                      onClick={() => toggleExpanded(agent.slug)}
                      className="w-full flex items-center justify-center gap-2 py-2 mb-4 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/10 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show Details
                        </>
                      )}
                    </button>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="space-y-4 mb-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                        {/* Endpoints */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Endpoints
                          </h4>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                Card:
                              </span>
                              <code className="flex-1 text-xs bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded break-all">
                                {agent.agent_card_url}
                              </code>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                                Tasks:
                              </span>
                              <code className="flex-1 text-xs bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded break-all">
                                {agent.tasks_endpoint}
                              </code>
                            </div>
                          </div>
                        </div>

                        {/* Capabilities */}
                        {agent.capabilities && agent.capabilities.length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Capabilities
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {agent.capabilities.map((cap, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 text-xs rounded-md"
                                >
                                  {cap}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Input Schema */}
                        {Object.keys(allInputs).length > 0 && (
                          <div>
                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Pipeline Inputs
                            </h4>
                            <div className="space-y-2">
                              {Object.entries(allInputs).map(
                                ([key, schema]: [string, any]) => (
                                  <div
                                    key={key}
                                    className="flex items-start gap-2 text-xs"
                                  >
                                    <code className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                                      {key}
                                    </code>
                                    <span className="text-gray-600 dark:text-gray-400">
                                      {schema.type}
                                      {requiredInputs.includes(key) && (
                                        <span className="ml-1 text-red-500">*</span>
                                      )}
                                    </span>
                                    {schema.description && (
                                      <span className="text-gray-500 dark:text-gray-500 italic flex-1">
                                        {schema.description}
                                      </span>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* A2A Protocol Parameters */}
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            A2A Protocol Parameters (Optional)
                          </h4>
                          <div className="space-y-2">
                            <div className="flex items-start gap-2 text-xs">
                              <code className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                                webhook_url
                              </code>
                              <span className="text-gray-600 dark:text-gray-400">
                                string
                              </span>
                              <span className="text-gray-500 dark:text-gray-500 italic flex-1">
                                Callback URL for async task completion notifications
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-xs">
                              <code className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                                webhook_auth
                              </code>
                              <span className="text-gray-600 dark:text-gray-400">
                                object
                              </span>
                              <span className="text-gray-500 dark:text-gray-500 italic flex-1">
                                Authentication config for webhook (type, token)
                              </span>
                            </div>
                            <div className="flex items-start gap-2 text-xs">
                              <code className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 px-2 py-1 rounded">
                                correlation_id
                              </code>
                              <span className="text-gray-600 dark:text-gray-400">
                                string
                              </span>
                              <span className="text-gray-500 dark:text-gray-500 italic flex-1">
                                Your tracking ID for correlating requests
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Sample Curl Command */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                              Example Request
                            </h4>
                            <button
                              onClick={() => {
                                const curlCommand = generateCurlCommand(agent, allInputs);
                                navigator.clipboard.writeText(curlCommand);
                                addNotification('Curl command copied to clipboard', 'success');
                              }}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                            >
                              <Copy className="w-3 h-3" />
                              Copy
                            </button>
                          </div>
                          <pre className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-3 rounded text-xs overflow-x-auto">
                            {generateCurlCommand(agent, allInputs)}
                          </pre>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            💡 Replace <code className="bg-gray-200 dark:bg-gray-700 px-1 rounded">YOUR_API_KEY</code> with an API key from the <a href="/api-keys" className="text-blue-600 dark:text-blue-400 hover:underline">API Keys page</a>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={() => handleCopyURL(agent.agent_card_url)}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm"
                        title="Copy agent card URL"
                      >
                        <Copy className="w-4 h-4" />
                        Copy URL
                      </button>
                      <a
                        href={agent.agent_card_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        title="View agent card"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => handleRemoveAgent(agent.slug)}
                        disabled={removingAgent === agent.slug}
                        className="flex items-center justify-center p-2 bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
                        title="Unpublish agent"
                      >
                        {removingAgent === agent.slug ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Agent Modal */}
      {showAddModal && (
        <AddA2AAgentModal
          onClose={() => setShowAddModal(false)}
          onSuccess={handleAgentAdded}
        />
      )}
    </div>
  );
};

